import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    Paper,
    Typography,
    makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import { connect } from 'react-redux';
import { Spinner } from '../../../components/UI';
import { EmptyIcon } from '../../../assets/svg';
import UploadPayEarnModal from '../../../components/Modals/UploadPayEarnModal';
import AddEditApproverModal from './AddEditApproverModal';
import { getCapacityApprovers } from '../../../store/actions/settingsActionCreator';
import { TIER_LABELS } from '../../../utils/capacityApproval';

// Same row-card list styling as Hubs & Shifts (HubList.js) — bold, non-uppercase headers and
// bordered/rounded/zebra-striped rows — so this Settings tab reads as one consistent system
// with the rest of Settings instead of its own bespoke table.
const useStyles = makeStyles(() => ({
    listItemHeader: {
        border: '0px',
        '& .MuiTypography-body1': {
            color: '#808080',
            fontSize: 16,
            fontWeight: 'bold',
        },
    },
    listItem: {
        padding: '16px',
        marginBottom: '8px',
        border: '1px solid #ebedf8',
        borderRadius: '8px',
        '& .MuiTypography-body1': {
            fontSize: 16,
            fontWeight: 400,
        },
        '&:nth-child(odd)': {
            backgroundColor: '#f8f9fa',
        },
        '&:hover': {
            backgroundColor: 'rgba(67,65,158,.1) !important',
        },
    },
}));

const COLUMN_WIDTHS = {
    tier: '12%',
    name: '20%',
    email: '26%',
    phone: '16%',
    org: '18%',
    action: '8%',
};

function Approvers({
    user,
    settings,
    isCapacityApproverDecider,
    slotCapacityMultiOrgUploadEnabled,
    dispatchGetCapacityApprovers,
}) {
    const classes = useStyles();
    const [loading, setLoading] = useState(false);
    const [approvers, setApprovers] = useState([]);
    const [roleChain, setRoleChain] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedApprover, setSelectedApprover] = useState(null);
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const currentOrgId = settings?.basicDetails?.id || user?.orgId;
    const currentOrgName = settings?.basicDetails?.name || currentOrgId;

    const fetchApprovers = useCallback(() => {
        setLoading(true);
        dispatchGetCapacityApprovers(
            (response) => {
                setLoading(false);
                setApprovers(response?.response?.approvers ?? []);
                setRoleChain(response?.response?.roleChain ?? []);
            },
            () => {
                setLoading(false);
            },
        );
    }, [dispatchGetCapacityApprovers]);

    useEffect(() => {
        fetchApprovers();
    }, [fetchApprovers]);

    const hasRows = !!(roleChain?.length || approvers?.length);

    return (
        <Spinner loading={loading}>
            <Paper style={{ padding: '1.5rem' }} elevation={0}>
                <Box style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}>
                    <Box>
                        <Typography variant="h6" style={{ fontWeight: 800, fontSize: 16 }}>
                            Approver
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ maxWidth: 560 }}>
                            {isCapacityApproverDecider
                                ? 'You can create and update Approver entries only. FM, SFM and Super Admin come from existing StaffPay roles and cannot be changed here.'
                                : 'Read-only. This is the approval chain that applies to your hub.'}
                        </Typography>
                    </Box>
                    <Box style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        {isCapacityApproverDecider && slotCapacityMultiOrgUploadEnabled && (
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => setUploadModalOpen(true)}
                                disableElevation
                            >
                                Upload (multi-org)
                            </Button>
                        )}
                        {isCapacityApproverDecider && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    setSelectedApprover(null);
                                    setModalOpen(true);
                                }}
                                disableElevation
                            >
                                Add Approver
                            </Button>
                        )}
                    </Box>
                </Box>
                {hasRows ? (
                    <List component="nav">
                        <ListItem className={classes.listItemHeader}>
                            <Box
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    width: '100%',
                                }}
                            >
                                <Box style={{ width: COLUMN_WIDTHS.tier }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Tier</Typography>
                                </Box>
                                <Box style={{ width: COLUMN_WIDTHS.name }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Name</Typography>
                                </Box>
                                <Box style={{ width: COLUMN_WIDTHS.email }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Email</Typography>
                                </Box>
                                <Box style={{ width: COLUMN_WIDTHS.phone }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Phone</Typography>
                                </Box>
                                <Box style={{ width: COLUMN_WIDTHS.org }}>
                                    <Typography style={{ fontWeight: 'bold' }}>Org</Typography>
                                </Box>
                                <Box style={{ width: COLUMN_WIDTHS.action }}> </Box>
                            </Box>
                        </ListItem>
                        {roleChain.map((row) => (
                            <ListItem
                                key={`role-${row.userId}-${row.tier}`}
                                className={classes.listItem}
                            >
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Box style={{ width: COLUMN_WIDTHS.tier }}>
                                        <Typography style={{ fontWeight: 600 }}>
                                            {TIER_LABELS[row?.tier] ?? row?.tier}
                                        </Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.name }}>
                                        <Typography style={{ fontWeight: 600 }}>{row?.name}</Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.email }}>
                                        <Typography>{row?.email || '-'}</Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.phone }}>
                                        <Typography>{row?.phone || '-'}</Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.org }}>
                                        <Typography>{currentOrgName}</Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.action }}>
                                        {isCapacityApproverDecider && (
                                            <Typography style={{ color: '#808080', fontSize: 12 }}>
                                                from StaffPay roles
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                        {approvers.map((row) => (
                            <ListItem key={row.id} className={classes.listItem}>
                                <Box
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%',
                                    }}
                                >
                                    <Box style={{ width: COLUMN_WIDTHS.tier }}>
                                        <Typography style={{ fontWeight: 600 }}>
                                            {TIER_LABELS.APPROVER}
                                        </Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.name }}>
                                        <Typography style={{ fontWeight: 600 }}>{row?.name}</Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.email }}>
                                        <Typography>{row?.email || '-'}</Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.phone }}>
                                        <Typography>{row?.phone || '-'}</Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.org }}>
                                        <Typography>{currentOrgName}</Typography>
                                    </Box>
                                    <Box style={{ width: COLUMN_WIDTHS.action }}>
                                        {isCapacityApproverDecider && (
                                            <IconButton
                                                style={{ borderRadius: 8, padding: 6 }}
                                                color="primary"
                                                onClick={() => {
                                                    setSelectedApprover(row);
                                                    setModalOpen(true);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    !loading && (
                        <Box
                            style={{
                                marginTop: 24,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: 40,
                            }}
                        >
                            <EmptyIcon />
                            <Typography style={{ marginTop: 8 }}>
                                No approvers configured yet
                            </Typography>
                        </Box>
                    )
                )}
                {!isCapacityApproverDecider && (
                    <Box
                        style={{
                            marginTop: 16,
                            background: '#fafbfd',
                            border: '1px solid #ebedf8',
                            borderRadius: 8,
                            padding: '12px 14px',
                        }}
                    >
                        <Typography variant="body2" style={{ fontWeight: 800, marginBottom: 4 }}>
                            Who reviews your requests
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            A change beyond your own limit is routed to the lowest tier above you
                            that is allowed to make a change of that size.
                        </Typography>
                    </Box>
                )}
            </Paper>
            {!!modalOpen && (
                <AddEditApproverModal
                    open={modalOpen}
                    data={selectedApprover}
                    orgId={currentOrgId}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedApprover(null);
                        fetchApprovers();
                    }}
                />
            )}
            {!!uploadModalOpen && (
                <UploadPayEarnModal
                    open={uploadModalOpen}
                    enumType="slots_multi_org"
                    data={{}}
                    onClose={() => setUploadModalOpen(false)}
                />
            )}
        </Spinner>
    );
}

const mapStateToProps = (state) => ({
    user: state?.user,
    settings: state?.settings,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchGetCapacityApprovers: (onSuccess, onError) =>
        dispatch(getCapacityApprovers(onSuccess, onError)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Approvers);
